import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Typography, 
  Tag, 
  Input, 
  Button,
  Space,
  Statistic,
  Row,
  Col,
  Spin,
  message,
  Modal,
  Form,
  InputNumber,
  Popconfirm
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  RiseOutlined, 
  FallOutlined,
  EditOutlined,
  StockOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { api } from '../utils/api';

const { Title } = Typography;
const { Search } = Input;

const Stocks = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();

  useEffect(() => {
  }, []);

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const response = await api.getStocks();
      setStocks(response.data);
    } catch (error) {
      message.error('Failed to fetch stocks');
    } finally {
      setLoading(false);
    }
  };
  const handleEditStock = (stock) => {
    setSelectedStock(stock);
    setEditModalVisible(true);
    form.setFieldsValue({
      currentPrice: stock.currentPrice,
      high: stock.high,
      low: stock.low,
      volume: stock.volume,
    });
  };

  const handleUpdateStock = async (values) => {
    try {
      await api.updateStock(selectedStock._id, values);
      
      message.success('Stock updated successfully');
      setEditModalVisible(false);
      fetchStocks();
    } catch (error) {
      message.error('Failed to update stock');
    }
  };

  const handleCreateStock = async (values) => {
    try {
      await api.createStock(values);
      message.success('Stock created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchStocks();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create stock');
    }
  };

  const handleDeleteStock = async (stockId) => {
    try {
      await api.deleteStock(stockId);
      message.success('Stock deleted successfully');
      fetchStocks();
    } catch (error) {
      message.error('Failed to delete stock');
    }
  };

  const filteredStocks = stocks.filter(stock =>
    stock.symbol?.toLowerCase().includes(searchText.toLowerCase()) ||
    stock.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const getStockStats = () => {
    const totalStocks = filteredStocks.length;
    const gainers = filteredStocks.filter(stock => stock.change > 0).length;
    const losers = filteredStocks.filter(stock => stock.change < 0).length;
    const avgChange = filteredStocks.reduce((sum, stock) => sum + (stock.changePercent || 0), 0) / totalStocks;

    return { totalStocks, gainers, losers, avgChange };
  };

  const stats = getStockStats();

  const columns = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol) => (
        <Tag color="blue" style={{ fontWeight: 'bold', fontSize: '14px', padding: '4px 12px' }}>
          {symbol}
        </Tag>
      ),
      sorter: (a, b) => a.symbol?.localeCompare(b.symbol),
      width: 120,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (name) => (
        <span style={{ fontWeight: '500', color: '#1e293b' }}>{name}</span>
      ),
    },
    {
      title: 'Current Price',
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      render: (price) => (
        <span style={{ fontWeight: '600', color: '#059669', fontSize: '16px' }}>
          ₹{price?.toFixed(2)}
        </span>
      ),
      align: 'right',
      sorter: (a, b) => a.currentPrice - b.currentPrice,
    },
    {
      title: 'Change',
      dataIndex: 'change',
      key: 'change',
      render: (change) => {
        const isPositive = change >= 0;
        return (
          <div style={{ 
            color: isPositive ? '#10b981' : '#ef4444',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4
          }}>
            {isPositive ? <RiseOutlined /> : <FallOutlined />}
            ₹{Math.abs(change).toFixed(2)}
          </div>
        );
      },
      align: 'right',
      sorter: (a, b) => a.change - b.change,
    },
    {
      title: 'Change %',
      dataIndex: 'changePercent',
      key: 'changePercent',
      render: (changePercent) => {
        const isPositive = changePercent >= 0;
        return (
          <Tag 
            color={isPositive ? 'green' : 'red'} 
            style={{ fontWeight: 'bold', fontSize: '12px' }}
          >
            {isPositive ? '+' : ''}{changePercent?.toFixed(2)}%
          </Tag>
        );
      },
      align: 'right',
      sorter: (a, b) => a.changePercent - b.changePercent,
    },
    {
      title: 'High',
      dataIndex: 'high',
      key: 'high',
      render: (high) => high ? (
        <span style={{ color: '#10b981', fontWeight: '500' }}>₹{high.toFixed(2)}</span>
      ) : '-',
      align: 'right',
    },
    {
      title: 'Low',
      dataIndex: 'low',
      key: 'low',
      render: (low) => low ? (
        <span style={{ color: '#ef4444', fontWeight: '500' }}>₹{low.toFixed(2)}</span>
      ) : '-',
      align: 'right',
    },
    {
      title: 'Volume',
      dataIndex: 'volume',
      key: 'volume',
      render: (volume) => volume ? (
        <span style={{ fontWeight: '500' }}>{volume.toLocaleString()}</span>
      ) : '-',
      align: 'right',
    },
    {
      title: 'Previous Close',
      dataIndex: 'previousClose',
      key: 'previousClose',
      render: (price) => (
        <span style={{ fontWeight: '500', color: '#64748b' }}>₹{price?.toFixed(2)}</span>
      ),
      align: 'right',
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (date) => (
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {new Date(date).toLocaleString()}
        </span>
      ),
      sorter: (a, b) => new Date(a.lastUpdated) - new Date(b.lastUpdated),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStock(record)}
            style={{ borderRadius: '6px' }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Stock"
            description="Are you sure you want to delete this stock?"
            onConfirm={() => handleDeleteStock(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ borderRadius: '6px' }}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 180,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: 32,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#1e293b', fontSize: '28px', fontWeight: 700 }}>
            Stocks Management
          </Title>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: '16px' }}>
            Monitor and manage stock prices and data
          </p>
        </div>
        <Space>
          <Search
            placeholder="Search stocks..."
            allowClear
            style={{ width: 320, maxWidth: '100%' }}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            size="large"
          />
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={fetchStocks}
            loading={loading}
            style={{ borderRadius: '8px', height: '40px' }}
          >
            Refresh Data
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
            style={{ borderRadius: '8px', height: '40px' }}
          >
            Add Stock
          </Button>
        </Space>
      </div>

      {/* Statistics */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white'
          }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Total Stocks</span>}
              value={stats.totalStocks}
              valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            border: 'none',
            color: 'white'
          }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Gainers</span>}
              value={stats.gainers}
              valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
              prefix={<RiseOutlined style={{ color: 'white' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            border: 'none',
            color: 'white'
          }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Losers</span>}
              value={stats.losers}
              valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
              prefix={<FallOutlined style={{ color: 'white' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            border: 'none',
            color: 'white'
          }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Avg Change %</span>}
              value={stats.avgChange}
              precision={2}
              suffix="%"
              valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Stocks Table */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StockOutlined style={{ color: '#3b82f6' }} />
            <span>All Stocks</span>
          </div>
        }
        className="slide-up"
      >
        <Table
          columns={columns}
          dataSource={filteredStocks}
          rowKey="_id"
          pagination={{
            total: filteredStocks.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} stocks`,
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* Create Stock Modal */}
      <Modal
        title="Add New Stock"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateStock}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="symbol"
                label="Stock Symbol"
                rules={[
                  { required: true, message: 'Please enter stock symbol' },
                  { pattern: /^[A-Z]+$/, message: 'Symbol must be uppercase letters only' }
                ]}
              >
                <Input 
                  size="large" 
                  placeholder="e.g., AAPL, GOOGL"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Company Name"
                rules={[
                  { required: true, message: 'Please enter company name' }
                ]}
              >
                <Input size="large" placeholder="e.g., Apple Inc." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="currentPrice"
                label="Current Price (₹)"
                rules={[
                  { required: true, message: 'Please enter current price' },
                  { type: 'number', min: 0, message: 'Price must be positive' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={2}
                  min={0}
                  size="large"
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="previousClose"
                label="Previous Close (₹)"
                rules={[
                  { required: true, message: 'Please enter previous close price' },
                  { type: 'number', min: 0, message: 'Price must be positive' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={2}
                  min={0}
                  size="large"
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)} size="large">
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Create Stock
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Stock Modal */}
      <Modal
        title="Edit Stock Data"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateStock}
        >
          <div style={{ 
            marginBottom: 24, 
            padding: 16, 
            background: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Tag color="blue" style={{ fontSize: '16px', padding: '8px 16px' }}>
                {selectedStock?.symbol}
              </Tag>
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#1e293b' }}>
                  {selectedStock?.name}
                </div>
              </div>
            </div>
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="currentPrice"
                label="Current Price (₹)"
                rules={[
                  { required: true, message: 'Please enter current price' },
                  { type: 'number', min: 0, message: 'Price must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={2}
                  min={0}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="volume"
                label="Volume"
                rules={[
                  { type: 'number', min: 0, message: 'Volume must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="high"
                label="High (₹)"
                rules={[
                  { type: 'number', min: 0, message: 'High must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={2}
                  min={0}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="low"
                label="Low (₹)"
                rules={[
                  { type: 'number', min: 0, message: 'Low must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={2}
                  min={0}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)} size="large">
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Update Stock
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Stocks;